import { useEffect, useState } from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatBRL } from '../../lib/currency';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { StatusBadge, STATUS_CHARGEBACK } from '../../components/admin/StatusBadge';

interface Chargeback {
  id: string; amount: number; reason: string | null; status: string;
  stripe_dispute_id: string | null; created_at: string; user_id: string;
  profiles: { email: string | null; full_name: string | null } | null;
}

/** Inline tooltip wrapper for the word "chargeback" */
function ChargebackTerm({ children }: { children: React.ReactNode }) {
  return (
    <span
      title="Chargeback: contestação de cobrança iniciada pelo cliente junto ao banco. O valor é bloqueado até resolução."
      style={{ borderBottom: '1px dotted var(--text-muted)', cursor: 'help' }}
    >
      {children}
    </span>
  );
}

export function AdminChargebacksPage() {
  const [chargebacks, setChargebacks] = useState<Chargeback[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [rate,        setRate]        = useState(0);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);

    const [{ data: disputes }, { count: totalPayments }] = await Promise.all([
      supabase
        .from('stripe_disputes')
        .select('id, amount, reason, status, stripe_dispute_id, created_at, user_id, profiles:user_id(email, full_name)')
        .order('created_at', { ascending: false })
        .limit(100),
      supabase
        .from('payments')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'succeeded')
        .gte('created_at', new Date(Date.now() - 30 * 86400000).toISOString()),
    ]);

    const rows = (disputes ?? []) as unknown as Chargeback[];
    setChargebacks(rows);

    const recentDisputes = rows.filter(d =>
      new Date(d.created_at) > new Date(Date.now() - 30 * 86400000)
    ).length;
    const pmt = totalPayments ?? 1;
    setRate(pmt > 0 ? (recentDisputes / pmt) * 100 : 0);
    setLoading(false);
  }

  function exportCsv() {
    const headers = ['Data', 'Usuário', 'Valor', 'Motivo', 'Status', 'ID Stripe'];
    const rows = chargebacks.map(c => [
      new Date(c.created_at).toLocaleDateString('pt-BR'),
      c.profiles?.email ?? c.user_id,
      String(c.amount),
      c.reason ?? '',
      c.status,
      c.stripe_dispute_id ?? '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `chargebacks_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  const needsResponseCount = chargebacks.filter(d => d.status === 'needs_response').length;

  return (
    <div>
      <AdminPageHeader
        title="Chargebacks"
        objective="Contestações abertas por clientes junto ao banco ou operadora de cartão. IMPORTANTE: Responda disputas com status 'Requer resposta' antes do prazo, o Stripe concede 5 a 10 dias úteis. Ultrapassar 0,5% do volume mensal pode suspender sua conta."
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
            {chargebacks.length} disputas totais
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={exportCsv}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <Download size={14} /> Exportar CSV
        </button>
      </div>

      {/* Alert banner for disputes needing response */}
      {!loading && needsResponseCount > 0 && (
        <div style={{ padding: '0.875rem 1rem', backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 'var(--radius-md)', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AlertTriangle size={16} color="#f87171" />
          <span style={{ fontSize: 'var(--text-label)', color: '#f87171', fontWeight: 600 }}>
            ⚠️ Você tem {needsResponseCount} {needsResponseCount === 1 ? 'disputa aguardando' : 'disputas aguardando'} resposta.
          </span>
        </div>
      )}

      {/* Rate card */}
      <div className="card" style={{ marginBottom: '1.5rem', border: rate > 0.5 ? '1px solid rgba(239,68,68,0.4)' : '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {rate > 0.5 && <AlertTriangle size={20} color="#f87171" />}
          <div>
            <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Taxa de <ChargebackTerm>chargeback</ChargebackTerm> (30 dias)
            </p>
            <p style={{ margin: '0.2rem 0 0', fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 700, color: rate > 0.5 ? '#f87171' : rate > 0.2 ? '#fbbf24' : '#4ade80' }}>
              {rate.toFixed(2)}%
            </p>
            <p style={{ margin: '0.2rem 0 0', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
              Limite Stripe: 0.5% · Limite Visa: 0.9%
              {rate > 0.5 && ', ⚠️ ACIMA DO LIMITE STRIPE!'}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card shimmer" style={{ height: '300px' }} />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>Data</th><th>Usuário</th><th>Valor</th><th>Motivo</th><th>Status</th><th>Prazo</th></tr>
            </thead>
            <tbody>
              {chargebacks.map(c => (
                <tr key={c.id}>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(c.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td>
                    <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600 }}>{c.profiles?.full_name ?? '-'}</p>
                    <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>{c.profiles?.email ?? c.user_id}</p>
                  </td>
                  <td style={{ fontWeight: 700, color: '#fca5a5', fontFamily: 'var(--font-display)' }}>{formatBRL(c.amount)}</td>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{c.reason ?? '-'}</td>
                  <td>
                    <StatusBadge {...(STATUS_CHARGEBACK[c.status] ?? STATUS_CHARGEBACK.closed)} />
                  </td>
                  <td style={{ fontSize: 'var(--text-caption)', color: c.status === 'needs_response' ? '#fbbf24' : 'var(--text-muted)' }}>
                    {c.status === 'needs_response' ? '5–10 dias úteis' : '-'}
                  </td>
                </tr>
              ))}
              {chargebacks.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#4ade80', fontStyle: 'italic', padding: '2rem' }}>
                  ✓ Nenhum <ChargebackTerm>chargeback</ChargebackTerm> registrado
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
